import { ColorPicker, ColorPickerChangeEvent, ColorPickerHandle, FlatColorPicker } from '@progress/kendo-react-inputs';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@progress/kendo-react-buttons';

import { autoUpdate, flip, FloatingPortal, shift, useDismiss, useFloating, useInteractions, useTransitionStyles } from '@floating-ui/react';
import { createPortal } from 'react-dom';
import { UNIVERSAL_MAP_COLOR_PALETTE_SETTINGS } from '../../../common/color-picker-palette';
import { RefreshOutline } from '../../../icons/icons';

interface ColorPickerWithDefaultProps {
    value?: string | null;
    defaultColor?: string;
    onChange?: (color: ColorPickerChangeEvent) => void;
    disabled?: boolean;
    palette?: {
        palette: string[];
        columns: number;
    };
    views?: ('palette' | 'gradient')[];
    id?: string;
}

export const ColorPickerWithDefault = ({
    value,
    defaultColor,
    onChange,
    id,
    disabled = false,
    palette = UNIVERSAL_MAP_COLOR_PALETTE_SETTINGS,
    views = ['palette', 'gradient']
}: ColorPickerWithDefaultProps) => {
    const [open, setOpen] = useState(false);
    const [previewValue, setPreviewValue] = useState(value);
    const [picker, setPicker] = useState<ColorPickerHandle | null>(null);
    const paletteWithDefault = useMemo(
        () => ({
            palette: defaultColor ? [defaultColor, ...palette.palette.filter((c) => c !== defaultColor)] : palette.palette,
            columns: palette.columns
        }),
        [defaultColor, palette]
    );

    // Sync value with preview if the popup is open
    useEffect(() => {
        if (open) {
            setPreviewValue(value);
        }
    }, [open, value]);

    useEffect(() => {
        const button = picker?.element?.querySelector('.k-button');
        if (!button) {
            return;
        }

        const handler = () => setOpen(true);
        (button as HTMLButtonElement).addEventListener('click', handler);

        return () => {
            button.removeEventListener('click', handler);
        };
    }, [picker?.element]);

    // Need to create our own popup here because Telerik doesn't expose selected preview color through its public API.
    // Resetting color to default triggers an onChange event which is not what we want, we only want to change preview color to default and then let user cancel or apply.

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        elements: {
            reference: picker?.element
        },
        middleware: [flip(), shift()],
        placement: 'bottom-start'
    });

    const dismiss = useDismiss(context);

    const { getFloatingProps } = useInteractions([dismiss]);

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: 100,
        initial: (params) => ({
            opacity: 0,
            transform: params.side === 'top' ? 'translateY(50px)' : 'translateY(-50px)'
        })
    });
    const container = useMemo(
        () => (refs.floating?.current as HTMLElement)?.querySelector('.k-coloreditor-footer'),
        [refs.floating.current, isMounted]
    );

    return (
        <>
            <ColorPicker
                ref={setPicker}
                value={value ?? undefined}
                open={false}
                onActiveColorClick={() => setOpen(true)}
                id={id}
                paletteSettings={paletteWithDefault}
                disabled={disabled}
                views={views}
            />

            <FloatingPortal>
                {isMounted && (
                    // Telerik puts popups at 11500 z-index
                    <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 11501 }} {...getFloatingProps()}> 
                        <div className="k-card" style={transitionStyles}>
                            <FlatColorPicker
                                value={previewValue ?? undefined}
                                paletteSettings={paletteWithDefault}
                                views={views}
                                onPreviewChange={(e) => setPreviewValue(e.value)}
                                defaultValue={defaultColor}
                                showClearButton={false}
                                onChange={(e) => {
                                    onChange?.({
                                        target: picker!,
                                        value: e.value!,
                                        syntheticEvent: e.syntheticEvent,
                                        nativeEvent: e.nativeEvent
                                    });
                                    setOpen(false);
                                }}
                                setOpen={setOpen}
                            />
                            {container &&
                                createPortal(
                                    <Button
                                        className="reset-button button-small"
                                        svgIcon={RefreshOutline}
                                        title="Reset to default"
                                        fillMode="flat"
                                        onClick={() => {
                                            setPreviewValue(defaultColor);
                                        }}
                                    />,
                                    container
                                )}
                        </div>
                    </div>
                )}
            </FloatingPortal>
        </>
    );
};

export default ColorPickerWithDefault;
